import "./styles.scss";

import axios from "axios";
import { Plugins } from "@capacitor/core";
import { orderBy } from "lodash";
import { BASE_URL } from "@/constants/constants";

const { Share, Modals, Device } = Plugins;

const ColorDropdown = () => import("../../components/color-dropdown/index.vue");
const LineThicknessDropdown = () =>
  import("../../components/line-thickness-dropdown/index.vue");
const ShapeDropdown = () => import("../../components/shape-dropdown/index.vue");
const StampDropdown = () => import("../../components/stamp-dropdown/index.vue");
const OpacityDropdown = () =>
  import("../../components/opacity-dropdown/index.vue");

const FreehandModeDropdown = () =>
  import("../../components/freehand-mode-dropdown/index.vue");
const ShapeModeDropdown = () =>
  import("../../components/shape-mode-dropdown/index.vue");
const StampModeDropdown = () =>
  import("../../components/stamp-mode-dropdown/index.vue");

const ShareDocumentModal = () =>
  import("../../components/share-document-modal/index.vue");

const rootUrl = BASE_URL;

import moment from "moment-timezone";

export default {
  name: "DocumentPage",

  data() {
    return {
      fetchError: false,
      errorMsg: "",
      page: 1,
      pages: 1,
      allow: false,
      dirty: false,
      documentJSON: {},
      mode: "zoom",
      documentLoadedUri: false,
      canUndo: false,
      ready: false,
      shareAvailable: false,
      webShareReady: false,
      readyCheck: null,
      canShare: true,
      setupComplete: false,
      lastAnnotation: false,
      eventListener: null,
      stamp: false,
      waiting_for: false,
      pdfRetryActive: false,
      statusMessage: "Preparing your document...",
      context: {
        primaryColor: "#FF0000",
        lineThickness: "5",
        opacity: 1.0,
        shape: "square",
        stampId: "1",
      },
    };
  },

  computed: {
    hasAnnotations() {
      if (!this.annotation) {
        return false;
      }
      return true;
    },

    updater() {
      return this.$store.getters.getAnnotationsBusy;
    },

    scribbleUrl() {
      if (this.allow) {
        return "/js/scribble/public/scribble.html";
      }
      return "about:blank";
    },

    desktop() {
      return this.$store.state.desktop;
    },

    user() {
      return this.$store.getters.getUser;
    },

    stamps() {
      return orderBy(this.$store.getters.getStamps, (stamp) =>
        stamp.label.toLowerCase(),
      );
    },

    allStamps() {
      return this.$store.getters.getStampCache;
    },

    stampsReady() {
      return this.$store.getters.getAllStampsLoaded;
    },

    userDisplayName() {
      return `${this.user.first_name} ${this.user.last_name}`;
    },

    document() {
      return this.$store.getters.getCurrentDocument(this.id);
    },

    backURL() {
      if (this.document && this.document.document_category === "preplans") {
        return "/action/pre-plans";
      } else {
        return "";
      }
    },

    id() {
      return Number(this.$route.params.id);
    },

    list() {
      return this.$store.getters.getDocumentsList("preplans");
    },

    canNext() {
      return this.page < this.pages;
    },

    canPrevious() {
      return this.page > 1;
    },

    canExport() {
      return this.webShareReady;
    },

    documentUri() {
      return this.documentLoadedUri;
    },

    annotation() {
      return this.$store.getters.getDocumentAnnotation(this.id);
    },

    last_saved() {
      const saved = this.$store.getters.getLastSaved;
      if (saved && saved.document === this.id) {
        return this.$store.getters.getLastSaved;
      }
      return false;
    },

    busy() {
      return this.$store.getters.getDocumentBusy;
    },

    building() {
      return this.$store.getters.getActiveBuilding;
    },

    smallScreen() {
      return window.matchMedia("(max-width: 360px)");
    },

    online() {
      //return false;
      return this.$store.getters.getOffline;
    },
  },

  watch: {
    online(newValue, oldValue) {
      this.setMode("zoom");
      if (newValue && !oldValue && (this.fetchError || !this.documentLoadedUri)) {
        this.prepareDocument();
      }
    },

    "$route.params.id"() {
      this.fetchError = false;
      this.documentLoadedUri = false;
      this.allow = false;
      this.prepareDocument();
    },

    statusMessage(newValue) {
      console.info(newValue);
    },
    stampsReady(newVal) {
      if (newVal && this.stamps.length > 0) {
        this.setFirstStamp();
      }
    },
  },

  created() {
    this.$store.commit("setMenuItems", this.list);
    this.$store.commit("setMenuDetails", {
      title: "Pre Plans",
      slug: "pre-plans",
      back_url: "/action",
    });

    if (this.online) {
      this.$store.dispatch("fetchBuildingProfile");
    }

    this.$store.dispatch("getDocumentsData");
    this.$store.dispatch("getStampsList");

    window.addEventListener("message", this.handleMessage, true);

    this._unwatchRetryVisible = this.$watch(
      () => (this.$retryPopup ? this.$retryPopup.visible : false),
      (visible) => {
        this.pdfRetryActive = visible;
      },
      { immediate: true },
    );

    this.prepareDocument();
  },

  mounted() {
    this.$store.commit("setActiveMenu", true);
    return true;
  },

  beforeDestroy() {
    window.removeEventListener("message", this.handleMessage, true);
    if (this._unwatchRetryVisible) {
      this._unwatchRetryVisible();
    }
    if (this.scribble) {
      this.scribble.removeEventListener("resize", this.onResize);
    }
    this.$store.commit("setActiveMenu", true);
    this.scribble = null;
    this.allow = false;
  },

  methods: {
    async prepareDocument() {
      this.fetchError = false;
      this.ready = false;

      try {
        const info = await Device.getInfo();
        if (info.platform === "android" || window.navigator.share !== undefined) {
          this.webShareReady = true;
        }

        if (this.online) {
          this.statusMessage = "Downloading Annotations...";
          await this.$store.dispatch("getAnnotationsList");
          this.statusMessage = "Loading Stamps...";
          if (this.allStamps.length < 1) {
            this.statusMessage = "Downloading Stamps...";
            await this.$store.dispatch("fetchAllStamps");
          }
        } else {
          this.statusMessage = "Loading offline cache...";
          await this.$store.dispatch("getAnnotationsList");
          await this.$store.dispatch("reloadStampCache");
        }

        await this.$store.dispatch("getStampsList");
        this.statusMessage = "Loading Data...";
        await this.$store.dispatch("getDocumentsData");

        this.statusMessage = "Loading PDF...";
        const blob = await this.$store.dispatch(
          "fetchDocumentURI",
          this.document.id,
        );

        if (blob) {
          this.documentLoadedUri = blob;
          this.allow = true;
        } else if (!this.online) {
          this.fetchError =
            "Your document could not be downloaded. Please contact support.";
        } else {
          this.fetchError =
            "Please connect to the internet to view this document.";
        }
      } catch (err) {
        console.error(err);
        if (this.$retryPopup && this.$retryPopup.cancelledByUser) {
          this.fetchError =
            "Please connect to the internet to view this document.";
          return;
        }
        this.fetchError =
          "Please connect to the internet to view this document.";
      }
    },

    async initScribble() {
      if (!this.setupComplete) {
        this.statusMessage = "Rendering PDF...";
        this.setupComplete = true;
        this.dirty = true;
        this.ready = false;
        this.scribble = document.querySelector("iframe").contentWindow;
        this.scribble.addEventListener("resize", this.onResize);
        this.sendMessage("setDocument", { document: this.documentUri });
        this.sendMessage("setContext", this.context);
        this.setMode("zoom");
        if (this.annotation) {
          this.statusMessage = "Waiting for annotations...";
        } else {
          this.statusMessage = "No annotations found...";
        }
      }
    },

    async setAnnotations() {
      if (!this.dirty) {
        this.statusMessage = "Nothing to update...";
      }
      if (this.annotation && this.dirty) {
        this.statusMessage = "Rendering annotations...";
        const annotations = await JSON.parse(this.annotation["json"]);
        const stamps = await this.$store.dispatch(
          "loadAssetsForAnnotations",
          annotations,
        );
        if (stamps.length > 0) {
          this.setFirstStamp();
          this.sendMessage("setLocalAssets", stamps);
        } else {
          this.setFirstStamp();
          this.sendMessage("setLocalAssets", []);
        }
        this.sendMessage("setAnnotation", {
          editable: true,
          annotation: annotations,
        });
        this.dirty = false;
        this.ready = true;
      } else if (this.dirty) {
        this.statusMessage = "Rendering document...";
        this.setFirstStamp();
        this.sendMessage("setAnnotation", { editable: true, annotation: [[]] });
        this.dirty = false;
        this.ready = true;
      } else {
        this.statusMessage = "Re-rendering document...";
        this.ready = true;
      }
    },

    formatDate(data) {
      return moment
        .tz(data, "MM-DD-YYYY HH:mm:ss", "America/New_York")
        .fromNow();
    },

    async reloadAnnotations() {
      const confirm = await Modals.confirm({
        title: "Are you sure?",
        message:
          "This will download the latest annotations from the server. Local, unsaved changes may be lost.",
      });
      if (confirm.value === true) {
        this.dirty = true;
        await this.$store.dispatch("fetchAnnotations");
        this.setAnnotations();
      }
      return confirm;
    },

    onResize() {
      this.changePage(this.page);
    },

    async setFirstStamp() {
      if (this.stamp === false && this.stamps.length > 0) {
        this.stamp = this.stamps[0];
        this.loadStampToScribble(this.stamp);
        this.context.stampId = this.stamp.id;
        this.sendMessage("setContext", this.context);
      }
    },

    openColorPopover(ev) {
      this.popover = null;

      this.$ionic.popoverController
        .create({
          component: ColorDropdown,
          cssClass: "color-dropdown",
          event: ev,
          translucent: true,
          componentProps: {
            data: {
              current: this.context.primaryColor,
            },
          },
        })
        .then((a) => {
          this.popover = a;
          this.popover.present();

          this.popover.onDidDismiss().then((result) => {
            if (result["data"]) {
              this.context.primaryColor = result["data"];
              this.sendMessage("setContext", this.context);
            }
          });
        });
    },

    openLineThicknessPopover(ev) {
      this.popover = null;

      this.$ionic.popoverController
        .create({
          component: LineThicknessDropdown,
          cssClass: "line-thickness-dropdown",
          event: ev,
          translucent: true,
          componentProps: {
            data: {
              current: this.context.lineThickness,
            },
          },
        })
        .then((a) => {
          this.popover = a;
          this.popover.present();

          this.popover.onDidDismiss().then((result) => {
            if (result["data"]) {
              this.context.lineThickness = result["data"];
              this.sendMessage("setContext", this.context);
            }
          });
        });
    },

    openShapePopover(ev) {
      this.popover = null;

      this.$ionic.popoverController
        .create({
          component: ShapeDropdown,
          cssClass: "shape-dropdown",
          event: ev,
          translucent: true,
          componentProps: {
            data: {
              current: this.context.shape,
            },
          },
        })
        .then((a) => {
          this.popover = a;
          this.popover.present();

          this.popover.onDidDismiss().then((result) => {
            if (result["data"]) {
              this.context.shape = result["data"];
              this.sendMessage("setContext", this.context);
            }
          });
        });
    },

    openStampPopover(ev) {
      this.popover = null;
      this.$ionic.popoverController
        .create({
          component: StampDropdown,
          cssClass: "stamp-dropdown",
          enableBackdropDismiss: false,
          keyboardClose: true,
          event: ev,
          translucent: true,
          componentProps: {
            data: {
              stamps: this.stamps,
              current: this.context.stampId,
            },
          },
        })
        .then((a) => {
          this.popover = a;
          this.popover.onDidDismiss().then((result) => {
            if (result["data"]) {
              this.stamp = result["data"];
              this.loadStampToScribble(this.stamp);
              this.context.stampId = result["data"].id;
              this.sendMessage("setContext", this.context);
            }
          });
          this.popover.present();
        });
    },

    async loadStampToScribble(stamp) {
      const data = await this.$store.dispatch("loadStampFromStorage", stamp);
      stamp.media = data;
      this.sendMessage("setLocalAssets", [stamp]);
    },

    openOpacityPopover(ev) {
      this.popover = null;

      this.$ionic.popoverController
        .create({
          component: OpacityDropdown,
          cssClass: "opacity-dropdown",
          event: ev,
          translucent: true,
          componentProps: {
            data: {
              current: this.context.opacity,
            },
          },
        })
        .then((a) => {
          this.popover = a;
          this.popover.present();

          this.popover.onDidDismiss().then((result) => {
            if (result["data"]) {
              this.context.opacity = result["data"];
              this.sendMessage("setContext", this.context);
            }
          });
        });
    },

    deleteElement() {
      this.sendMessage("deleteElement", {});
    },

    clear() {
      this.$ionic.alertController
        .create({
          header: "Clear Drawing?",
          message:
            "This action CANNOT be undone. Do you want to delete all markups and return to original?",
          buttons: [
            {
              text: "Yes",
              handler: () => {
                this.sendMessage("clear", {});
              },
            },
            {
              text: "Cancel",
            },
          ],
        })
        .then((a) => a.present());
    },

    undo() {
      this.sendMessage("undo", {});
    },

    next() {
      if (this.canNext) {
        this.changePage(this.page + 1);
      }
    },

    previous() {
      if (this.canPrevious) {
        this.changePage(this.page - 1);
      }
    },

    changePage(page) {
      this.page = parseInt(page, 10);
      this.sendMessage("setPage", { page: parseInt(page, 10) });
    },

    setFreehandMode(evt) {
      this.setMode("freehand");

      if (!this.desktop) {
        this.popover = null;

        this.$ionic.popoverController
          .create({
            component: FreehandModeDropdown,
            cssClass: "freehand-mode-dropdown",
            event: evt,
            translucent: true,
            componentProps: {
              data: {
                lineThickness: this.context.lineThickness,
                primaryColor: this.context.primaryColor,
              },
            },
          })
          .then((a) => {
            this.popover = a;
            this.popover.present();

            this.popover.onDidDismiss().then((result) => {
              if (result["data"]) {
                this.context.primaryColor = result["data"].primaryColor;
                this.context.lineThickness = result["data"].lineThickness;
                this.sendMessage("setContext", this.context);
              }
            });
          });
      }
    },

    setArrowMode(evt) {
      this.setMode("arrow");

      if (!this.desktop) {
        this.openColorPopover(evt);
      }
    },

    setShapeMode(evt) {
      this.setMode("shape");

      if (!this.desktop) {
        this.popover = null;

        this.$ionic.popoverController
          .create({
            component: ShapeModeDropdown,
            cssClass: "shape-mode-dropdown",
            event: evt,
            translucent: true,
            componentProps: {
              data: {
                lineThickness: this.context.lineThickness,
                primaryColor: this.context.primaryColor,
                shape: this.context.shape,
              },
            },
          })
          .then((a) => {
            this.popover = a;
            this.popover.present();

            this.popover.onDidDismiss().then((result) => {
              if (result["data"]) {
                this.context.primaryColor = result["data"].primaryColor;
                this.context.lineThickness = result["data"].lineThickness;
                this.context.shape = result["data"].shape;
                this.sendMessage("setContext", this.context);
              }
            });
          });
      }
    },

    setStampMode(evt) {
      this.setMode("stamp");
      if (this.stamp) {
        this.loadStampToScribble(this.stamp);
      } else {
        this.setFirstStamp();
      }
      if (!this.desktop) {
        this.popover = null;

        this.$ionic.popoverController
          .create({
            component: StampModeDropdown,
            cssClass: "stamp-mode-dropdown",
            event: evt,
            translucent: true,
            componentProps: {
              data: {
                opacity: this.context.opacity,
                stamp: this.context.stampId,
                stamps: this.stamps,
              },
            },
          })
          .then((a) => {
            this.popover = a;
            this.popover.present();
            this.popover.onDidDismiss().then((result) => {
              if (result["data"]) {
                this.context.opacity = result["data"].opacity;
                if (typeof result["data"].stamp === "object") {
                  this.stamp = result["data"].stamp;
                  this.loadStampToScribble(this.stamp);
                  this.context.stampId = result["data"].stamp.id;
                } else {
                  this.context.stampId = result["data"].stamp;
                }
                this.sendMessage("setContext", this.context);
              }
            });
          });
      }
    },

    setMode(mode) {
      this.mode = mode;
      this.sendMessage("setMode", { mode });
    },

    setContext(type, e) {
      this.context[type] = e.srcElement.value;
      this.sendMessage("setContext", this.context);
    },

    sendMessage(command, data) {
      if (this.scribble) {
        this.scribble.postMessage({ command, data }, "*");
      }
    },

    handleMessage(evt) {
      const { command, data } = evt.data;

      if (command === "init") {
        this.initScribble();
      }

      if (command === "ready") {
        this.setAnnotations();
      }

      if (command === "setPageCount") {
        this.pages = data.pages;
      }

      if (command === "setUndo") {
        window.clearTimeout(this.waiting_for);
        this.canUndo = data.count;
        this.shareAvailable = false;
        window.setTimeout(async () => {
          this.sendMessage("requestAnnotation", { requestId: this.id });
        });
      }

      if (command === "setAnnotation") {
        if (data.requestId === this.id) {
          this.statusMessage = "Updating server...";
          window.clearTimeout(this.waiting_for);
          const document_id = this.id;
          const annotation_id =
            this.annotation && this.annotation.id ? this.annotation.id : null;
          const annotation_json = JSON.parse(JSON.stringify(data.annotation));
          const annotation = annotation_json;
          const new_data = { document_id, annotation, annotation_id };
          this.shareAvailable = false;
          this.$store.commit("addAnnotationState", new_data);
          this.waiting_for = window.setTimeout(async () => {
            try {
              this.statusMessage = "Annotations updated on server...";
              this.$store.dispatch("processAnnotationQueue");
              this.$store.dispatch("getAnnotationsList");
              window.clearTimeout(this.waiting_for);
              this.waiting_for = false;
            } catch (err) {
              this.statusMessage = "Annotations could not be updated.";
              window.clearTimeout(this.waiting_for);
            }
          }, 1000);
        }
      }
    },

    async shareAnnotations() {
      if (this.updater) {
        const should_stop = await Modals.confirm({
          title: "Are you sure?",
          message:
            "You have local changes which are still being saved to the server. Do you want to continue?",
        });
        if (should_stop.value !== true) {
          return;
        }
      }

      let annotation = this.$store.getters.getLastSaved;
      if (!annotation) {
        annotation = this.$store.getters.getDocumentAnnotation(this.id);
      }

      this.$store.dispatch("fetchBuildingProfile");

      this.$ionic.modalController
        .create({
          component: ShareDocumentModal,
          componentProps: {
            propsData: { annotations: annotation, document: this.document },
          },
        })
        .then((m) => m.present());
    },

    isWindowsDesktop() {
      return this.desktop && /Windows/i.test(navigator.userAgent);
    },

    buildShareIntro() {
      const intro = `${this.userDisplayName} would like to share the document "${this.document.name}" with you:`;
      return intro.charAt(0).toUpperCase() + intro.slice(1);
    },

    buildSharePayload(documentUrl) {
      const url = String(documentUrl || "").trim();
      const intro = this.buildShareIntro();

      if (this.isWindowsDesktop()) {
        // New Outlook for Windows ignores `url` unless the link is also in `text`.
        // URL on its own line (plain) + `url` field (clickable hyperlink below).
        return {
          text: `${intro}\n\n${url}`,
          url,
        };
      }

      return {
        text: `${intro}\n\n`,
        url,
      };
    },

    async exportSharedDocument(documentUrl) {
      const shareData = this.buildSharePayload(documentUrl);
      if (!shareData.url) {
        return;
      }

      // Pass only text + url (no title) so New Outlook receives both fields.
      if (typeof window.navigator.share === "function") {
        await window.navigator.share(shareData);
        return;
      }

      await Share.share({
        text: shareData.text,
        url: shareData.url,
        dialogTitle: "Share Document and Annotations...",
      });
    },

    async shareDocument() {
      if (this.updater) {
        const should_stop = await Modals.confirm({
          title: "Are you sure?",
          message:
            "You have local changes which are still being saved to the server. Do you want to continue?",
        });
        if (should_stop.value !== true) {
          return;
        }
      }

      if (!this.hasAnnotations && this.updater) {
        await Modals.confirm({
          title: "Document Busy",
          message:
            "Your annotations are still being saved to the server. Please wait a moment, and try again.",
        });
        return;
      }

      if (!this.hasAnnotations) {
        await Modals.confirm({
          title: "Unsharable",
          message:
            "Before sharing, you must make at least one annotation to this document, and the change must be saved. Saving is automatic, but is dependent on the speed of your internet connection.",
        });
        return;
      }

      if (this.shareAvailable) {
        await this.exportSharedDocument(this.shareAvailable.document);
      } else {
        const spinner = await this.$ionic.loadingController.create({
          message: "One moment while we prepare your file...",
        });

        spinner.present();

        let response;
        const data = { document_id: this.id };
        const headers = {
          Authorization: this.$store.getters.getAuthorizationHeaders,
        };

        try {
          response = await axios.post(
            `${rootUrl}/account/annotations/pdf/`,
            data,
            { headers: headers, timeout: 150000 },
          );
        } catch (err) {
          const file_error = await this.$ionic.loadingController.create({
            message:
              "There was an issue with your connection. Please try again. If the issue persists, contact support.",
            spinner: null,
            backdropDismiss: true,
            animated: true,
          });
          file_error.present();
          spinner.dismiss();
          return;
        }

        // Make sure the user data is loaded
        this.$store.dispatch("getUserData");

        if (response.status === 201 && response.data.document !== null) {
          this.shareAvailable = response.data;
          spinner.dismiss();
          try {
            await this.exportSharedDocument(this.shareAvailable.document);
          } catch (e) {
            const sharing = await this.$ionic.loadingController.create({
              message:
                'Your file is ready for export. Please click on the "Export to..." icon again.',
              spinner: null,
              backdropDismiss: true,
              duration: 5000,
              animated: false,
            });
            sharing.present();
          }
        } else if (response.data.document === null) {
          spinner.dismiss();
          const file_error = await this.$ionic.loadingController.create({
            message:
              "Your file could not be generated as it has become corrupted. Please delete all annotations and try again.",
            spinner: null,
            backdropDismiss: true,
            animated: false,
          });
          file_error.present();
        } else {
          spinner.dismiss();
          const file_error = await this.$ionic.loadingController.create({
            message:
              "Your file could not be generated. Please try again. If the issue persist, contact support.",
            spinner: null,
            backdropDismiss: true,
            animated: false,
          });
          file_error.present();
        }
      }

      return true;
    },
  },
};
